// Parser de CFDI (XML del SAT) — usa DOMParser nativo, sin dependencias.
// Soporta CFDI 3.3 y 4.0. Extrae folio fiscal (UUID), montos e impuestos
// y los mapea a los códigos de impuesto que usa el sistema.

// Códigos SAT: 001=ISR, 002=IVA, 003=IEPS
const aprox = (a, b) => Math.abs(a - b) < 0.0005;

// Busca por localName ignorando el prefijo de namespace (cfdi:, tfd:, etc.)
function first(root, localName) {
  const els = root.getElementsByTagNameNS('*', localName);
  return els.length ? els[0] : null;
}
function all(root, localName) {
  return Array.from(root.getElementsByTagNameNS('*', localName));
}
// Hijo directo por localName (ignora prefijo de namespace). Se usa para tomar
// el nodo <Impuestos> resumen del Comprobante y NO el de un concepto.
function directChild(parent, localName) {
  for (const child of Array.from(parent.childNodes || [])) {
    if (child.nodeType === 1 && child.localName === localName) return child;
  }
  return null;
}
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
// Redondeo a 2 decimales (evita el error binario: 90000.0000001 → 90000).
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Mapea un impuesto del CFDI a un código del sistema, o null si no aplica.
function mapImpuesto({ tipo, impuesto, tasa, importe, base }) {
  const t = tasa || (base ? importe / base : 0);
  if (tipo === 'traslado') {
    if (impuesto === '002' && aprox(t, 0.16)) return 'IVA_16';
    if (impuesto === '003') return 'IEPS_TRAS'; // IEPS trasladado (tasa variable: 3%, 8%, 26.5%…)
    return null; // IVA 8% / 0% no tienen código en el sistema
  }
  // retención
  const es4 = aprox(t, 0.04);                             // 4% (autotransporte de carga)
  if (impuesto === '002') {
    // IVA retenido: 4% (autotransporte) → RET_4; 10.6667% (honorarios, 2/3 de
    // 16%) → RET_IVA_10667; el resto (16%) → RET_IVA_16.
    if (es4)               return 'RET_4';
    if (aprox(t, 0.106667)) return 'RET_IVA_10667';
    return 'RET_IVA_16';
  }
  if (impuesto === '001') {
    if (aprox(t, 0.0125)) return 'RET_ISR_125';
    if (es4)              return 'RET_4';                 // ISR 4%
    if (aprox(t, 0.10))   return 'RET_ISR_10';
    return 'RET_ISR_10';                                  // ISR sin tasa estándar → 10%
  }
  if (impuesto === '003') return 'RET_IEPS';
  if (es4)                return 'RET_4';
  return null;
}

// Parsea una cadena XML de CFDI. Lanza Error si no es un CFDI válido.
export function parseCFDI(xmlString, nombreArchivo = '') {
  const doc = new DOMParser().parseFromString(xmlString, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new Error(`${nombreArchivo}: XML no válido`);
  }
  const comp = first(doc, 'Comprobante');
  if (!comp) throw new Error(`${nombreArchivo}: no es un CFDI (falta Comprobante)`);

  const serie = comp.getAttribute('Serie') || '';
  const folio = comp.getAttribute('Folio') || '';
  const subTotal = num(comp.getAttribute('SubTotal'));
  const descuento = num(comp.getAttribute('Descuento'));
  const total = num(comp.getAttribute('Total'));
  const moneda = comp.getAttribute('Moneda') || 'MXN';
  const fecha = (comp.getAttribute('Fecha') || '').split('T')[0];
  // Método de pago del CFDI: PUE (pago en una exhibición) o PPD (parcialidades/diferido)
  const metodoPago = comp.getAttribute('MetodoPago') || comp.getAttribute('metodoPago') || '';
  // Forma de pago del SAT (clave 01 efectivo, 03 transferencia, 99 por definir...).
  // Es distinta de `formaPago` de la solicitud, que es texto libre del sistema.
  const formaPagoSat = comp.getAttribute('FormaPago') || comp.getAttribute('formaPago') || '';
  // Tipo de comprobante: 'I' Ingreso (factura), 'E' Egreso (nota de crédito),
  // 'P' Pago, etc. Una nota de crédito (Egreso) debe RESTAR, no sumar.
  const tipoComprobante = comp.getAttribute('TipoDeComprobante') || comp.getAttribute('tipoDeComprobante') || '';
  const esEgreso = tipoComprobante.toUpperCase() === 'E';

  const emisor = first(comp, 'Emisor');
  const rfcEmisor = emisor?.getAttribute('Rfc') || emisor?.getAttribute('rfc') || '';
  const nombreEmisor = emisor?.getAttribute('Nombre') || '';

  // Uso del CFDI (clave del receptor, ej. G03 = Gastos en general)
  const receptor = first(comp, 'Receptor');
  const usoCfdi = receptor?.getAttribute('UsoCFDI') || receptor?.getAttribute('usoCFDI') || '';

  const tfd = first(comp, 'TimbreFiscalDigital');
  const uuidCrudo = (tfd?.getAttribute('UUID') || tfd?.getAttribute('uuid') || '').trim();
  // Algunos emisores (CFE, entre otros) entregan el XML SIN TIMBRAR: el nodo del
  // timbre viene con los marcadores de la plantilla sin sustituir, con UUID="UUID"
  // y los sellos vacíos. Ese texto no es un folio fiscal, y tomarlo como tal hacía
  // que todas esas facturas compartieran identidad y se marcaran como duplicadas
  // entre sí. Solo se acepta como UUID lo que tenga forma de UUID.
  const esUUID = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  const uuid = esUUID(uuidCrudo) ? uuidCrudo : '';
  // Sin timbre no hay comprobante fiscal válido: no ampara el gasto ni permite
  // acreditar el IVA. Se deja pasar (a veces se captura antes de tener el timbrado)
  // pero se marca para advertirlo en pantalla.
  const sinTimbrar = !uuid;
  // Fecha de timbrado del CFDI (del TimbreFiscalDigital) — solo la fecha (YYYY-MM-DD)
  const fechaTimbrado = (tfd?.getAttribute('FechaTimbrado') || tfd?.getAttribute('fechaTimbrado') || '').split('T')[0] || '';

  // Concepto(s): descripción de los <Concepto> del CFDI
  const descripciones = all(comp, 'Concepto')
    .map(c => (c.getAttribute('Descripcion') || '').trim())
    .filter(Boolean);
  const concepto = descripciones.length
    ? (descripciones.length === 1 ? descripciones[0] : `${descripciones[0]} (+${descripciones.length - 1})`)
    : '';

  // Desglose por concepto (para el split de la Solicitud de Débito): importe base de
  // cada concepto y su IVA 16% (0 si es exento). Permite mandar la comisión al gasto
  // y el fondo/asignación a otra cuenta.
  const conceptos = all(comp, 'Concepto').map(c => {
    const importe = num(c.getAttribute('Importe'));
    const desc = (c.getAttribute('Descripcion') || '').trim();
    const impC = directChild(c, 'Impuestos');
    let iva = 0;
    if (impC) for (const tr of all(impC, 'Traslado')) {
      if (tr.getAttribute('Impuesto') === '002' && aprox(num(tr.getAttribute('TasaOCuota')), 0.16))
        iva += num(tr.getAttribute('Importe'));
    }
    return { descripcion: desc, importe: round2(importe), iva: round2(iva) };
  });

  // Base gravable = lo que se grava (subtotal menos descuento)
  const base = Math.max(0, subTotal - descuento);

  // Impuestos.
  // Se prefieren los nodos a NIVEL CONCEPTO porque siempre traen Base y
  // TasaOCuota, con lo que la tasa real es exacta. El resumen global suele
  // omitir la tasa (p.ej. una retención de IVA al 4% aparece solo con Importe),
  // y al inferirla de importe/base daría un valor equivocado. Solo se recurre
  // al resumen global (por categoría) cuando no hay nodos a nivel concepto.
  const codigos = new Set();
  // Importe REAL de cada impuesto tal como viene en el XML (suma por código).
  // Con esto el neto cuadra al peso con el CFDI aunque una retención aplique
  // solo a parte de la factura (bases parciales).
  const importes = {};
  const nodoGlobal = directChild(comp, 'Impuestos');

  const cTraslados = [], cRetenciones = [];
  for (const c of all(comp, 'Concepto')) {
    const imp = directChild(c, 'Impuestos');
    if (!imp) continue;
    cTraslados.push(...all(imp, 'Traslado'));
    cRetenciones.push(...all(imp, 'Retencion'));
  }

  const traslados   = cTraslados.length   ? cTraslados   : (nodoGlobal ? all(nodoGlobal, 'Traslado')  : []);
  const retenciones = cRetenciones.length ? cRetenciones : (nodoGlobal ? all(nodoGlobal, 'Retencion') : []);

  // IVA trasladado TOTAL, sin importar la tasa. Va aparte de impuestosImportes
  // porque ahí sólo cabe IVA_16: el 8% de frontera y el 0% no tienen código en el
  // sistema (ver mapImpuesto), y sin este número no se puede cuadrar un pago
  // contra una factura que mezcla tasas.
  let ivaTrasladado = 0;
  for (const tr of traslados) {
    if (tr.getAttribute('Impuesto') === '002') ivaTrasladado += num(tr.getAttribute('Importe'));
    const cod = mapImpuesto({
      tipo: 'traslado',
      impuesto: tr.getAttribute('Impuesto'),
      tasa: num(tr.getAttribute('TasaOCuota')),
      importe: num(tr.getAttribute('Importe')),
      base: num(tr.getAttribute('Base')) || base,
    });
    if (cod) { codigos.add(cod); importes[cod] = (importes[cod] || 0) + num(tr.getAttribute('Importe')); }
  }
  for (const re of retenciones) {
    const cod = mapImpuesto({
      tipo: 'retencion',
      impuesto: re.getAttribute('Impuesto'),
      tasa: num(re.getAttribute('TasaOCuota')),
      importe: num(re.getAttribute('Importe')),
      // El resumen global no trae Base ni TasaOCuota en las retenciones;
      // la tasa se infiere de importe/base del comprobante.
      base: num(re.getAttribute('Base')) || base,
    });
    if (cod) { codigos.add(cod); importes[cod] = (importes[cod] || 0) + num(re.getAttribute('Importe')); }
  }

  // Redondear cada importe a 2 decimales (como el SAT) para que la suma cuadre
  // exactamente con el Total del CFDI y no queden cifras con muchos decimales.
  for (const k of Object.keys(importes)) importes[k] = round2(importes[k]);

  return {
    uuid,
    folioFiscal: uuid,   // vacío si el CFDI no está timbrado; usar `referencia` como respaldo
    sinTimbrar,
    serie, folio, fecha,
    tipoComprobante, esEgreso,
    rfcEmisor, nombreEmisor, usoCfdi, metodoPago, formaPagoSat, fechaTimbrado,
    concepto, conceptos,
    subTotal, descuento, total, moneda,
    base,
    impuestos: [...codigos],
    impuestosImportes: importes,   // importe exacto por código, tomado del XML
    ivaTrasladado: round2(ivaTrasladado),
    // identificador legible para mostrar
    referencia: uuid || [serie, folio].filter(Boolean).join('-') || nombreArchivo,
  };
}

// Datos fiscales del CFDI que se guardan junto a cada factura o concepto, para
// que el reporte los pueda mostrar sin volver a abrir el XML.
export const CAMPOS_FISCALES = [
  'rfcEmisor', 'nombreEmisor', 'folioComprobante', 'fechaEmision',
  'subTotal', 'descuento', 'totalCfdi', 'monedaCfdi', 'tipoComprobante', 'formaPagoSat',
];

// Extrae esos campos del resultado de parseCFDI (nombres del parser → nombres guardados).
export function datosFiscales(cfdi = {}) {
  return {
    rfcEmisor: cfdi.rfcEmisor || '',
    nombreEmisor: cfdi.nombreEmisor || '',
    folioComprobante: cfdi.folio || '',   // el folio propio del emisor, no el UUID
    fechaEmision: cfdi.fecha || '',       // fecha del comprobante, no la de timbrado
    subTotal: cfdi.subTotal,
    descuento: cfdi.descuento,
    totalCfdi: cfdi.total,
    monedaCfdi: cfdi.moneda || '',
    tipoComprobante: cfdi.tipoComprobante || '',
    formaPagoSat: cfdi.formaPagoSat || '',
  };
}

// Toma solo esos campos de una factura/concepto ya armado, al enviar al backend.
export const soloFiscales = (o = {}) => Object.fromEntries(CAMPOS_FISCALES.map(k => [k, o[k]]));

// Etiquetas legibles de los códigos de impuesto internos (ver mapImpuesto arriba).
export const IMPUESTO_LABEL = {
  IVA_16:        'IVA 16%',
  RET_IVA_10667: 'IVA retención 10.67%',
  RET_IVA_16:    'IVA retención 16%',
  RET_ISR_125:   'Retención ISR 1.25%',
  RET_ISR_10:    'ISR retención 10%',
  RET_4:         'Retención 4%',
  RET_IEPS:      'Retención IEPS 26.5%',
  IEPS_TRAS:     'IEPS trasladado',
};

// Traslados y retenciones de un mapa de importes, separados. Los códigos que
// empiezan con 'RET' restan; el resto suma. MISMA regla que backend/src/utils/neto.js,
// que es quien calcula el neto definitivo: se mantienen espejo.
export function desglosarImpuestos(impuestosImportes = {}) {
  let traslados = 0, retenciones = 0;
  for (const [k, v] of Object.entries(impuestosImportes)) {
    const n = Number(v) || 0;
    if (String(k).startsWith('RET')) retenciones += n; else traslados += n;
  }
  return { traslados: round2(traslados), retenciones: round2(retenciones) };
}

// Neto a pagar = base + traslados − retenciones.
export function netoConImpuestos(base, impuestosImportes = {}) {
  const { traslados, retenciones } = desglosarImpuestos(impuestosImportes);
  return round2((Number(base) || 0) + traslados - retenciones);
}

// Lee un File y devuelve su texto.
export function leerArchivo(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error(`No se pudo leer ${file.name}`));
    r.readAsText(file);
  });
}
